import { Granularity, Metric } from '../dashboard-config.model';
import { GRANULARITY_PROFILES, GranularityTrait } from '../granularities/granularity-profile';
import type { MetricEntityLabels } from '../metrics/metric-profile';

/** Hidden parent filter holding the dashboard's metric (`metric` is the legacy company-type filter). */
export const METRIC_ID_FILTER_NAME = 'metric_id';
/** Filter holding the selected KPI id. */
export const KPI_FILTER_NAME = 'kpi';

/**
 * One selectable measure (KPI) across all metrics. Single source of truth for
 * KPI labels, dataset columns, units and availability.
 * See KPI-FILTER-FINDINGS.md §2 and PROFILE-REMODEL-FINDINGS.md §2.
 */
export interface KpiOption {
  /** Globally unique; equals the dataset column stem (derived columns are `${value}${suffix}`). */
  value: KpiId;
  /** Parent key for the `kpi` filter (child of the hidden `metric_id` filter). */
  metricId: Metric;
  /** Nominative: dropdown, `kpi_name` row key, library titles. */
  label: string;
  /** Genitive / plural: chart headings ("ENTWICKLUNG DER … BESCHÄFTIGTEN"). */
  headingLabel: string;
  /** Genitive plural without article, for counts: "ANZAHL GESAMTBESCHÄFTIGTER". */
  genitiveLabel?: string;
  /** Full genitive phrase where the KPI is the headline noun (GVA). */
  headingPhrase?: string;
  /** Value column; differs from `value` only by the `__k_eur` suffix. */
  valueColumn: string;
  unit: 'count' | 'kEur';
  /** Noun forms where the KPI, not the metric, is what is counted (GVA: Produktionswert). */
  entityLabels?: MetricEntityLabels;
  /** Granularity traits whose datasets do not contain this KPI. */
  unavailableAt?: readonly GranularityTrait[];
  /** Pre-remodel filter name and option value, for parity checks and saved-slice aliases. */
  legacy?: { filterName: string; value: string };
}

export type KpiId =
  | 'companies_count'
  | 'small_companies_count'
  | 'companies_revenue'
  | 'small_companies_revenue'
  | 'gross_value_added'
  | 'production_value'
  | 'economic_participants_total'
  | 'economic_participants_core_market'
  | 'economic_participants_marginal'
  | 'employed_total'
  | 'employed_social_security'
  | 'employed_marginal'
  | 'solo_self_employed_count'
  | 'overnight_stays_total'
  | 'overnight_stays_foreign'
  | 'overnight_stays_domestic';

/** Ordered per metric: the first KPI of a metric is its default. */
export const KPI_OPTIONS: readonly KpiOption[] = [
  /* Companies count */
  {
    value: 'companies_count',
    metricId: Metric.CompaniesCount,
    label: 'Unternehmen',
    headingLabel: 'Unternehmen',
    genitiveLabel: 'Unternehmen',
    valueColumn: 'companies_count',
    unit: 'count',
    legacy: { filterName: 'metric', value: 'companies' },
  },
  {
    value: 'small_companies_count',
    metricId: Metric.CompaniesCount,
    label: 'Kleinunternehmen',
    headingLabel: 'Kleinunternehmen',
    genitiveLabel: 'Kleinunternehmen',
    valueColumn: 'small_companies_count',
    unit: 'count',
    unavailableAt: ['district-level'],
    legacy: { filterName: 'metric', value: 'small_companies' },
  },

  /* Companies revenue */
  {
    value: 'companies_revenue',
    metricId: Metric.CompaniesRevenue,
    label: 'Unternehmen',
    headingLabel: 'Unternehmen',
    valueColumn: 'companies_revenue__k_eur',
    unit: 'kEur',
    legacy: { filterName: 'metric', value: 'companies' },
  },
  {
    value: 'small_companies_revenue',
    metricId: Metric.CompaniesRevenue,
    label: 'Kleinunternehmen',
    headingLabel: 'Kleinunternehmen',
    valueColumn: 'small_companies_revenue__k_eur',
    unit: 'kEur',
    unavailableAt: ['district-level'],
    legacy: { filterName: 'metric', value: 'small_companies' },
  },

  /* Gross value added: the KPI is the headline noun itself */
  {
    value: 'gross_value_added',
    metricId: Metric.GrossValueAdded,
    label: 'Bruttowertschöpfung',
    headingLabel: 'Bruttowertschöpfung',
    headingPhrase: 'DER BRUTTOWERTSCHÖPFUNG',
    valueColumn: 'gross_value_added__k_eur',
    unit: 'kEur',
    legacy: { filterName: 'gross_value_added', value: 'gross_value_added' },
  },
  {
    value: 'production_value',
    metricId: Metric.GrossValueAdded,
    label: 'Produktionswert',
    headingLabel: 'Produktionswert',
    headingPhrase: 'DES PRODUKTIONSWERTS',
    entityLabels: {
      structureLabel: 'Produktionswertstruktur',
      entityAfterDerLabel: 'Produktionswerts',
      genitiveArticle: 'des',
      standaloneEntityLabel: 'Produktionswert',
    },
    valueColumn: 'production_value__k_eur',
    unit: 'kEur',
    legacy: { filterName: 'gross_value_added', value: 'production_value' },
  },

  /* Economic participants */
  {
    value: 'economic_participants_total',
    metricId: Metric.EconomicParticipant,
    label: 'Erwerbstätige',
    headingLabel: 'Erwerbstätigen',
    genitiveLabel: 'Erwerbstätiger',
    valueColumn: 'economic_participants_total',
    unit: 'count',
    unavailableAt: ['district-level'],
    legacy: { filterName: 'economic_participant', value: 'total' },
  },
  {
    value: 'economic_participants_core_market',
    metricId: Metric.EconomicParticipant,
    label: 'Kernerwerbstätige',
    headingLabel: 'Kernerwerbstätigen',
    genitiveLabel: 'Kernerwerbstätiger',
    valueColumn: 'economic_participants_core_market',
    unit: 'count',
    legacy: { filterName: 'economic_participant', value: 'core_market' },
  },
  {
    value: 'economic_participants_marginal',
    metricId: Metric.EconomicParticipant,
    label: 'Geringfügig Erwerbstätige',
    headingLabel: 'Geringfügig Erwerbstätigen',
    genitiveLabel: 'Geringfügig Erwerbstätiger',
    valueColumn: 'economic_participants_marginal',
    unit: 'count',
    unavailableAt: ['district-level'],
    legacy: { filterName: 'economic_participant', value: 'marginal' },
  },

  /* Employed */
  {
    value: 'employed_total',
    metricId: Metric.Employed,
    label: 'Gesamtbeschäftigte',
    headingLabel: 'Gesamtbeschäftigten',
    genitiveLabel: 'Gesamtbeschäftigter',
    valueColumn: 'employed_total',
    unit: 'count',
    legacy: { filterName: 'employed', value: 'total' },
  },
  {
    value: 'employed_social_security',
    metricId: Metric.Employed,
    label: 'Sv-pflichtig Beschäftigte',
    headingLabel: 'Sv-pflichtig Beschäftigten',
    genitiveLabel: 'Sv-pflichtig Beschäftigter',
    valueColumn: 'employed_social_security',
    unit: 'count',
    legacy: { filterName: 'employed', value: 'social_security' },
  },
  {
    value: 'employed_marginal',
    metricId: Metric.Employed,
    label: 'Geringfügig Beschäftigte',
    headingLabel: 'Geringfügig Beschäftigten',
    genitiveLabel: 'Geringfügig Beschäftigter',
    valueColumn: 'employed_marginal',
    unit: 'count',
    legacy: { filterName: 'employed', value: 'marginal' },
  },

  /* Self-employed: single KPI, no type filter today */
  {
    value: 'solo_self_employed_count',
    metricId: Metric.SelfEmployed,
    label: 'Solo-Selbstständige',
    headingLabel: 'Solo-Selbstständigen',
    genitiveLabel: 'Solo-Selbstständiger',
    valueColumn: 'solo_self_employed_count',
    unit: 'count',
  },

  /* Tourism */
  {
    value: 'overnight_stays_total',
    metricId: Metric.Tourism,
    label: 'Gesamt',
    headingLabel: 'Gesamt',
    valueColumn: 'overnight_stays_total',
    unit: 'count',
    legacy: { filterName: 'tourists', value: 'total' },
  },
  {
    value: 'overnight_stays_foreign',
    metricId: Metric.Tourism,
    label: 'Ausländisch',
    headingLabel: 'Ausländisch',
    valueColumn: 'overnight_stays_foreign',
    unit: 'count',
    legacy: { filterName: 'tourists', value: 'foreign' },
  },
  {
    value: 'overnight_stays_domestic',
    metricId: Metric.Tourism,
    label: 'Inländisch',
    headingLabel: 'Inländisch',
    valueColumn: 'overnight_stays_domestic',
    unit: 'count',
    legacy: { filterName: 'tourists', value: 'domestic' },
  },
];

export const KPI_BY_ID = Object.fromEntries(
  KPI_OPTIONS.map((option) => [option.value, option]),
) as Record<KpiId, KpiOption>;

/** KPIs of one metric, in default order. */
export function kpisOfMetric(metricId: Metric): KpiOption[] {
  return KPI_OPTIONS.filter((option) => option.metricId === metricId);
}

/** Whether the KPI's datasets exist at the given granularity. */
export function isKpiAvailableAt(option: KpiOption, granularity: Granularity | undefined): boolean {
  if (granularity === undefined || !option.unavailableAt?.length) {
    return true;
  }
  const traits = GRANULARITY_PROFILES[granularity]?.traits ?? [];
  return !option.unavailableAt.some((trait) => traits.includes(trait));
}

/** KPI selected in filter values, if any. */
export function selectedKpi(filterValues: Map<string, any>): KpiOption | undefined {
  return KPI_BY_ID[filterValues.get(KPI_FILTER_NAME) as KpiId];
}

/**
 * Filter values saved before the KPI filter existed (library slices) carry the
 * metric's legacy type filter instead of `kpi`. Adds the matching `kpi` value;
 * returns the input unchanged when `kpi` is present or nothing matches.
 */
export function withLegacyKpiFilterValue(filterValues: Map<string, any>, metricId: Metric): Map<string, any> {
  if (filterValues.get(KPI_FILTER_NAME) != null) {
    return filterValues;
  }

  const kpi = kpisOfMetric(metricId).find(
    (option) => option.legacy && filterValues.get(option.legacy.filterName) === option.legacy.value,
  );
  return kpi ? new Map([...filterValues, [KPI_FILTER_NAME, kpi.value]]) : filterValues;
}

/** The same type under another metric, e.g. small_companies_count → small_companies_revenue. */
export function counterpartKpi(kpi: KpiOption, metricId: Metric): KpiOption | undefined {
  return kpi.legacy && kpiFromLegacyValue(metricId, kpi.legacy.filterName, kpi.legacy.value);
}

/** KPI for a pre-remodel filter value, e.g. (CompaniesRevenue, 'metric', 'companies'). */
export function kpiFromLegacyValue(
  metricId: Metric,
  filterName: string,
  value: string,
): KpiOption | undefined {
  return KPI_OPTIONS.find(
    (option) =>
      option.metricId === metricId &&
      option.legacy?.filterName === filterName &&
      option.legacy.value === value,
  );
}
